/**
 * Utility functions and constants for TMResultsDataTable component
 * 
 * This file contains:
 * - Static constants (column names, operators, error messages, etc.)
 * - Helper functions for data manipulation and formatting
 * - Filter and sort utility functions
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Filter operators (V3 Annexure API standard)
 * Used in filter builder for building complex filter queries
 */
export const FILTER_OPERATORS = [
  { label: 'Exact', value: 'exact' },
  { label: 'Not Equals', value: 'not_equals' },
  { label: 'Contains', value: 'contains' },
  { label: 'Not Contains', value: 'not_contains' },
  { label: 'Starts With', value: 'starts_with' },
  { label: 'Ends With', value: 'ends_with' },
  { label: 'Greater Than', value: 'greater_than' },
  { label: 'Greater Than or Equal', value: 'greater_than_or_equal' },
  { label: 'Less Than', value: 'less_than' },
  { label: 'Less Than or Equal', value: 'less_than_or_equal' },
  { label: 'Is Null', value: 'is_null' },
  { label: 'Is Not Null', value: 'is_not_null' }
];

/**
 * Columns to exclude from table display
 * These columns are internal fields not meant for user viewing
 */
export const EXCLUDED_COLUMNS = ['_id'];

/**
 * Default column for new filter conditions
 */
export const DEFAULT_FILTER_COLUMN = 'Company Code';

/**
 * Default operator for new filter conditions
 */
export const DEFAULT_FILTER_OPERATOR = 'exact';

/**
 * Maximum file size for bulk manual matching uploads (in bytes)
 */
export const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

/**
 * Allowed file extensions for bulk manual matching uploads
 */
export const ALLOWED_FILE_EXTENSIONS = ['xlsx', 'xls', 'xlsm', 'xlsb'];

/**
 * Default pagination limit per page
 */
export const DEFAULT_PAGE_LIMIT = 50;

/**
 * Maximum number of records allowed for Excel export.
 * Generate Excel button is disabled when totalCount exceeds this limit.
 */
export const MAX_EXCEL_EXPORT_RECORDS = 700000;

/**
 * Operators that require a value input
 * Operators not in this list (like is_null, is_not_null) don't need a value
 */
export const OPERATORS_REQUIRING_VALUE = [
  'exact',
  'not_equals',
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'greater_than',
  'greater_than_or_equal',
  'less_than',
  'less_than_or_equal'
];

/**
 * Status values for status indicator styling
 */
export const STATUS_VALUES = {
  MATCHED: 'Matched',
  UNMATCHED: 'Unmatched',
  PENDING: 'Pending'
};

/**
 * Template download filter configuration
 * Used when downloading template with unmatched/manual records
 */
export const TEMPLATE_FILTER_CONFIG = {
  groups: [
    {
      id: 'template_filter_manual',
      name: 'Manual Match Nature',
      connector: 'AND',
      conditions: [
        {
          column: 'Match Nature',
          operator: 'contains',
          value: 'Manual'
        }
      ]
    },
    {
      id: 'template_filter_unmatched',
      name: 'Unmatched Records',
      connector: 'AND',
      conditions: [
        {
          column: 'Consol Status',
          operator: 'contains',
          value: 'Unmatched'
        }
      ]
    }
  ],
  group_connectors: ['OR']
};

/**
 * Error messages used across the component
 */
export const ERROR_MESSAGES = {
  NO_COLLECTION: 'No consolidated collection found',
  LOAD_DATA_FAILED: 'Failed to load consolidated data',
  NO_FILE_SELECTED: 'No file selected or wrong file selected',
  FILE_SIZE_EXCEEDED: 'Total file size exceeds the limit (100 MB)',
  UNSUPPORTED_FILE_TYPE: 'has an unsupported extension. Only xlsx, xls, xlsm, xlsb are allowed',
  UPLOAD_FAILED: 'Some files failed to upload',
  VALIDATION_FAILED: 'Validation failed. Please review the errors below.',
  UNKNOWN_ERROR: 'Unknown error!',
  UNEXPECTED_ERROR: 'Unexpected error occurred.',
  CANNOT_REMOVE_LAST_GROUP: 'Cannot remove the last filter group'
};

/**
 * Success messages
 */
export const SUCCESS_MESSAGES = {
  BULK_MATCH_SUBMITTED: 'Bulk manual matching submitted successfully'
};

/**
 * Column type prefixes from fileConfigInfo
 * Used to determine data type and formatting
 */
export const COLUMN_TYPE_PREFIXES = {
  AMOUNT: 'amount',
  NUMBER: 'number',
  STRING: 'string',
  DATE: 'date'
};

// ============================================================================
// UTILITY FUNCTIONS - ID Generation and Object Creation
// ============================================================================

/**
 * Generates a unique ID for filter groups and conditions
 * @returns {string} Unique identifier
 */
export function generateUniqueId() {
  return Math.random().toString(36).substr(2, 9);
}

/**
 * Creates a default filter condition object
 * @returns {Object} Default condition object with unique ID
 */
export function createDefaultCondition() {
  return {
    id: generateUniqueId(),
    column: DEFAULT_FILTER_COLUMN,
    operator: DEFAULT_FILTER_OPERATOR,
    value: ''
  };
}

/**
 * Creates a default filter group object
 * @returns {Object} Default filter group object with unique ID and one condition
 */
export function createDefaultFilterGroup() {
  return {
    id: generateUniqueId(),
    name: '',
    connector: 'AND',
    conditions: [createDefaultCondition()],
  };
}

/**
 * Checks if a filter object has a valid structure with groups array.
 * @param {Object} filters - Filter object to validate
 * @returns {boolean} True if filter has valid structure
 */
export function isValidFilterStructure(filters) {
  return filters && 
         Array.isArray(filters.groups) && 
         filters.groups.length > 0;
}

/**
 * Ensures all filter groups and conditions have unique IDs.
 * Used when loading filters from API that may be missing IDs.
 * Returns null if the filter structure is invalid or empty, allowing the caller
 * to fall back to a default empty filter structure.
 * @param {Object} filters - Filter object with groups and group_connectors
 * @returns {Object|null} Filter object with all IDs ensured, or null if invalid
 */
export function ensureFilterIds(filters) {
  // Return null for invalid/empty filters so caller can use default
  if (!isValidFilterStructure(filters)) {
    return null;
  }

  return {
    ...filters,
    group_connectors: filters.group_connectors || [],
    groups: filters.groups.map(group => ({
      ...group,
      id: group.id || generateUniqueId(),
      conditions: (group.conditions || []).map(condition => ({
        ...condition,
        id: condition.id || generateUniqueId()
      }))
    }))
  };
}

// ============================================================================
// UTILITY FUNCTIONS - Formatting
// ============================================================================

/**
 * Format number for display with proper formatting
 * - Negative numbers shown in parentheses
 * - Numbers formatted with thousand separators
 * - Handles null/undefined/empty values
 * 
 * @param {number|string} num - Number to format (can be string or number)
 * @returns {string} Formatted number string
 */
export function formatNumber(num) {
  // Handle null/undefined/empty
  if (num === null || num === undefined || num === '') return '';
  
  // Convert string to number if needed
  const numValue = typeof num === 'string' ? parseFloat(num) : num;
  
  // Return empty if not a valid number
  if (isNaN(numValue)) return '';
  
  if (numValue < 0) {
    return '(' + Math.abs(numValue).toLocaleString('en-US', { maximumFractionDigits: 0 }) + ')';
  }
  
  return numValue.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/**
 * Format header text - replace underscores with spaces
 * @param {string} text - Header text
 * @returns {string} Formatted text with spaces instead of underscores
 */
export function formatHeaderText(text) {
  return (text || '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ============================================================================
// UTILITY FUNCTIONS - Column Type Detection
// ============================================================================

/**
 * Parse a columnmap entry from setup.fileConfig[id].columnmap.
 * Supports legacy array [type, label] and v2 object { type, label } shapes.
 * @param {Array|Object|string} entry - columnmap entry
 * @returns {{ columnType: string|null, columnName: string|null }}
 */
export function parseColumnmapEntry(entry) {
  if (Array.isArray(entry) && entry.length >= 2) {
    return { columnType: entry[0], columnName: entry[1] };
  }

  if (entry && typeof entry === 'object') {
    return {
      columnType: entry.type || null,
      columnName: entry.label || entry.name || entry.header || null,
    };
  }

  if (typeof entry === 'string') {
    return { columnType: null, columnName: entry };
  }

  return { columnType: null, columnName: null };
}

/**
 * Build a map of column names to their data types from fileConfigInfo.
 * When fileConfigId is provided, only that config's columnmap is used (V2 tabs).
 * Structure: setup.fileConfig[id].columnmap — each entry has .type (e.g. date1) and label.
 *
 * @param {Object} fileConfigInfo - File configuration object from loadTransactionMatchingAnalysis
 * @param {string} [fileConfigId] - Optional file config ID to scope column types to one table
 * @returns {Object} Map of column name -> type (string1, date1, amount1, etc.)
 */
export function buildColumnTypeMap(fileConfigInfo, fileConfigId = '') {
  const typeMap = {};

  if (!fileConfigInfo || typeof fileConfigInfo !== 'object') {
    return typeMap;
  }

  const configEntries = fileConfigId && fileConfigInfo[fileConfigId]
    ? [[fileConfigId, fileConfigInfo[fileConfigId]]]
    : Object.entries(fileConfigInfo);

  for (const [, fileConfig] of configEntries) {
    const columnmap = fileConfig?.columnmap || [];

    for (let index = 0; index < columnmap.length; index++) {
      const { columnType, columnName } = parseColumnmapEntry(columnmap[index]);

      if (columnName && columnType) {
        typeMap[columnName] = columnType;
      }
    }
  }

  return typeMap;
}

/**
 * Check if a column is numeric based on its type
 * Types come as 'amount1', 'amount2', 'number1', etc.
 * 
 * @param {string} columnType - Column type from columnTypeMap
 * @returns {boolean} True if column should be formatted as numeric
 */
export function isNumericColumn(columnType) {
  if (!columnType) return false;
  return columnType.startsWith(COLUMN_TYPE_PREFIXES.AMOUNT) || 
         columnType.startsWith(COLUMN_TYPE_PREFIXES.NUMBER);
}

/**
 * Check if a column is a date type from fileConfigInfo (e.g. date1, date2).
 * @param {string} columnType - Column type from columnTypeMap
 * @returns {boolean}
 */
export function isDateColumn(columnType) {
  if (!columnType) return false;
  return columnType.startsWith(COLUMN_TYPE_PREFIXES.DATE);
}

/**
 * Whether a column is a date type from fileConfig columnmap.
 * @param {string} fieldName - Column name
 * @param {Object} columnTypeMap - Map of column names to types from fileConfig
 * @returns {boolean}
 */
export function isDateField(fieldName, columnTypeMap = {}) {
  return isDateColumn(columnTypeMap[fieldName]);
}

/**
 * Whether a column is an amount/number type from fileConfig columnmap.
 * @param {string} fieldName - Column name
 * @param {Object} columnTypeMap - Map of column names to types from fileConfig
 * @returns {boolean}
 */
export function isNumericField(fieldName, columnTypeMap = {}) {
  return isNumericColumn(columnTypeMap[fieldName]);
}

/**
 * Escape regex metacharacters in user input before embedding in $regex / $regexMatch.
 * @param {string} value - Raw filter value
 * @returns {string}
 */
export function escapeRegex(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// ============================================================================
// UTILITY FUNCTIONS - CSS Class Generation
// ============================================================================

/**
 * Get CSS class for a table cell based on column type
 * @param {Object} header - Header configuration with key and isNumeric flag
 * @param {Object} columnTypeMap - Map of column names to types
 * @returns {string} CSS class string
 */
export function getCellClass(header, columnTypeMap) {
  const classes = ['consolidated-results__cell'];
  
  // Get the column type from the type map (e.g., 'string1', 'date1', 'amount1')
  const columnType = columnTypeMap[header.key] || '';
  
  // Apply formatting based on data type prefix
  if (columnType.startsWith(COLUMN_TYPE_PREFIXES.AMOUNT) || 
      columnType.startsWith(COLUMN_TYPE_PREFIXES.NUMBER) || 
      header.isNumeric) {
    classes.push('consolidated-results__cell--number');
  }
  
  if (columnType.startsWith(COLUMN_TYPE_PREFIXES.STRING)) {
    // String columns can have monospace font for better readability
    classes.push('consolidated-results__cell--mono');
  }
  
  if (columnType.startsWith(COLUMN_TYPE_PREFIXES.DATE)) {
    // Date columns centered for better alignment
    classes.push('consolidated-results__cell--center');
  }
  
  return classes.join(' ');
}

/**
 * Get status indicator CSS class for the square color indicator
 * @param {string} status - Status value
 * @returns {string} CSS class string
 */
export function getStatusIndicatorClass(status) {
  const baseClass = 'consolidated-results__status-indicator';
  
  switch (status) {
    case STATUS_VALUES.MATCHED:
      return `${baseClass} ${baseClass}--matched`;
    case STATUS_VALUES.UNMATCHED:
      return `${baseClass} ${baseClass}--unmatched`;
    default:
      return `${baseClass} ${baseClass}--pending`;
  }
}

/**
 * Get CSS class for numeric values (handles negative numbers)
 * @param {number|string} value - Numeric value (can be string or number)
 * @returns {string} CSS class if negative, empty string otherwise
 */
export function getNumberClass(value) {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (!isNaN(numValue) && numValue < 0) {
    return 'consolidated-results__cell--number-negative';
  }
  return '';
}

// ============================================================================
// UTILITY FUNCTIONS - Table Headers
// ============================================================================

/**
 * Build table headers from first data record
 * Determines numeric columns based on columnTypeMap from fileConfigInfo
 * 
 * @param {Array} data - Array of data records
 * @param {Object} columnTypeMap - Map of column names to types
 * @returns {Array} Array of header objects with key, label, and isNumeric properties
 */
export function buildTableHeaders(data, columnTypeMap) {
  if (!data || data.length === 0) {
    return [];
  }

  const firstRecord = data[0];
  return Object.keys(firstRecord)
    .filter(key => !EXCLUDED_COLUMNS.includes(key))
    .map(key => {
      const columnType = columnTypeMap[key] || '';
      return {
        key,
        label: formatHeaderText(key),
        // Determine if numeric based on columnTypeMap from fileConfigInfo
        isNumeric: isNumericColumn(columnType)
      };
    });
}

// ============================================================================
// UTILITY FUNCTIONS - Filtering
// ============================================================================

/**
 * Check if operator requires a value input
 * @param {string} operator - Operator value (exact, contains, is_null, etc.)
 * @returns {boolean} True if operator requires value input
 */
export function operatorRequiresValue(operator) {
  return operator !== 'is_null' && operator !== 'is_not_null';
}

/**
 * Safely convert a value to a trimmed string
 * Handles cases where value might be a number, null, undefined, or already a string
 * @param {*} value - Value to convert
 * @returns {string} Trimmed string value
 */
function toTrimmedString(value) {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

/**
 * Check if a condition has a valid (non-empty) value
 * @param {Object} condition - Condition object with value property
 * @returns {boolean} True if condition has a non-empty value
 */
function hasValidValue(condition) {
  if (condition.value === null || condition.value === undefined) return false;
  // For numbers, check if it's a valid number
  if (typeof condition.value === 'number') return !isNaN(condition.value);
  // For strings, check if trimmed value is non-empty
  return String(condition.value).trim() !== '';
}

/**
 * Build filter payload for API request (V3 Annexure API standard)
 * Filters out groups with empty conditions and builds proper structure
 * 
 * @param {Array} filterGroups - Array of filter group objects
 * @param {string} groupingConnector - Global connector between groups ('AND' or 'OR')
 * @param {Object} columnTypeMap - Map of column names to their types (optional, used for numeric conversion)
 * @returns {Object} Filter payload matching V3 API structure
 */
export function buildFilterPayload(filterGroups, groupingConnector, columnTypeMap = {}) {
  // Filter out groups with empty conditions
  // A condition is valid if:
  // 1. It has a value (for operators that require value)
  // 2. OR it's a null operator (is_null, is_not_null)
  const validGroups = filterGroups.filter(group => 
    group.conditions.some(c => 
      !operatorRequiresValue(c.operator) || hasValidValue(c)
    )
  );

  if (validGroups.length === 0) {
    return { groups: [], group_connectors: [] };
  }

  // Build groups with V3 API structure
  const groups = validGroups.map(group => ({
    id: group.id,
    name: group.name || null,
    connector: group.connector, // AND/OR for conditions within this group
    conditions: group.conditions
      .filter(c => !operatorRequiresValue(c.operator) || hasValidValue(c))
      .map(c => {
        const conditionObj = {
          id: c.id || generateUniqueId(), // Preserve condition ID for reactivity
          column: c.column,
          operator: c.operator // exact, contains, starts_with, is_null, etc.
        };
        
        // Only include value for operators that require it
        if (operatorRequiresValue(c.operator)) {
          const columnType = columnTypeMap[c.column] || '';
          
          // Convert to number if column is numeric type (amount1, amount2, number1, etc.)
          if (isNumericColumn(columnType)) {
            // Value might already be a number from API, or a string from user input
            const numericValue = typeof c.value === 'number' ? c.value : parseFloat(toTrimmedString(c.value));
            // Use numeric value if valid, otherwise fall back to trimmed string
            conditionObj.value = !isNaN(numericValue) ? numericValue : toTrimmedString(c.value);
          } else {
            conditionObj.value = toTrimmedString(c.value);
          }
        }
        
        return conditionObj;
      })
  }));

  // Build connectors between groups using global groupingConnector
  const group_connectors = validGroups.slice(1).map(() => groupingConnector);

  return { groups, group_connectors };
}

/**
 * Convert V3 API filter operator to MongoDB operator
 * @param {string} operator - V3 API operator (exact, contains, starts_with, etc.)
 * @param {string} value - Filter value
 * @returns {Object} MongoDB query object
 */
export function convertOperatorToMongoQuery(operator, value) {
  switch (operator) {
    case 'exact':
      return { $eq: value };
    case 'not_equals':
      return { $ne: value };
    case 'contains':
      return { $regex: value, $options: 'i' };
    case 'not_contains':
      return { $not: { $regex: value, $options: 'i' } };
    case 'starts_with':
      return { $regex: `^${value}`, $options: 'i' };
    case 'ends_with':
      return { $regex: `${value}$`, $options: 'i' };
    case 'greater_than':
      return { $gt: parseFloat(value) || value };
    case 'greater_than_or_equal':
      return { $gte: parseFloat(value) || value };
    case 'less_than':
      return { $lt: parseFloat(value) || value };
    case 'less_than_or_equal':
      return { $lte: parseFloat(value) || value };
    case 'is_null':
      return { $in: [null, ''] };
    case 'is_not_null':
      return { $nin: [null, ''] };
    default:
      return { $eq: value };
  }
}

const TYPED_REGEX_OPERATORS = new Set([
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
]);

const DATE_COMPARISON_OPERATORS = new Set([
  'greater_than',
  'greater_than_or_equal',
  'less_than',
  'less_than_or_equal',
]);

/**
 * $toString expression — works whether the field is stored as string, number, or date.
 * @param {string} fieldName - Column name (may contain spaces)
 * @returns {Object}
 */
function getFieldStringExpr(fieldName) {
  return { $ifNull: [{ $toString: `$${fieldName}` }, ''] };
}

const COMPARISON_OPERATOR_MAP = {
  greater_than: '$gt',
  greater_than_or_equal: '$gte',
  less_than: '$lt',
  less_than_or_equal: '$lte',
  exact: '$eq',
  not_equals: '$ne',
};

/**
 * Coerce any filter value to a string for comparison operators.
 * @param {*} value - Filter value
 * @returns {string}
 */
function coerceFilterValueToComparisonString(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
}

/**
 * Parse YYYY-MM-DD into calendar-day string boundaries.
 * @param {string} value - Filter value
 * @returns {{ day: string, nextDay: string }|null}
 */
function parseCalendarDay(value) {
  const trimmed = coerceFilterValueToComparisonString(value);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return null;
  }

  const [year, month, day] = trimmed.split('-').map(Number);
  const nextDay = new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
  return { day: trimmed, nextDay };
}

/**
 * Whether regex filters should use $toString inside $expr (date/number columns).
 * @param {string} fieldName - Column name
 * @param {Object} columnTypeMap - Column type map
 * @returns {boolean}
 */
function shouldUseExprForRegex(fieldName, columnTypeMap) {
  return isDateField(fieldName, columnTypeMap) || isNumericField(fieldName, columnTypeMap);
}

/**
 * $convert to double — handles BSON numbers and numeric strings.
 * @param {string} fieldName - Column name
 * @returns {Object}
 */
function getFieldAsDoubleExpr(fieldName) {
  return {
    $convert: {
      input: `$${fieldName}`,
      to: 'double',
      onError: null,
      onNull: null,
    },
  };
}

/**
 * @param {*} value - Filter value
 * @returns {number|null}
 */
function coerceNumericFilterValue(value) {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  const trimmed = String(value).trim();
  const numericValue = parseFloat(trimmed);
  return Number.isNaN(numericValue) ? null : numericValue;
}

/**
 * Build a direct field comparison using string literals on both sides.
 * Works when the column is stored as a string (e.g. Document Number).
 * @param {string} fieldName - Column name
 * @param {string} mongoOp - $gte, $lt, etc.
 * @param {string} compareStr - String comparison value
 * @returns {Object}
 */
function buildDirectStringComparison(fieldName, mongoOp, compareStr) {
  return { [fieldName]: { [mongoOp]: compareStr } };
}

/**
 * Build $expr comparison with $toString on the DB field and a string literal.
 * @param {string} fieldName - Column name
 * @param {string} mongoOp - $gte, $lt, etc.
 * @param {string} compareStr - String comparison value
 * @returns {Object}
 */
function buildExprStringComparison(fieldName, mongoOp, compareStr) {
  return {
    $expr: {
      [mongoOp]: [getFieldStringExpr(fieldName), compareStr],
    },
  };
}

/**
 * Build $expr numeric comparison via $convert (BSON number or numeric string).
 * @param {string} fieldName - Column name
 * @param {string} mongoOp - $gte, $lt, etc.
 * @param {number} numericValue - Numeric comparison value
 * @returns {Object}
 */
function buildNumericExprComparison(fieldName, mongoOp, numericValue) {
  return {
    $expr: {
      [mongoOp]: [getFieldAsDoubleExpr(fieldName), numericValue],
    },
  };
}

/**
 * Emit a comparison using the correct strategy for the column type.
 * @param {string} fieldName - Column name
 * @param {string} mongoOp - $gte, $lt, etc.
 * @param {string} compareStr - String comparison value
 * @param {Object} columnTypeMap - Column type map
 * @param {number|null} numericValue - Parsed numeric value when applicable
 * @returns {Object}
 */
function buildFieldComparison(fieldName, mongoOp, compareStr, columnTypeMap, numericValue) {
  if (isNumericField(fieldName, columnTypeMap) && numericValue !== null) {
    return buildNumericExprComparison(fieldName, mongoOp, numericValue);
  }

  if (isDateField(fieldName, columnTypeMap)) {
    return buildExprStringComparison(fieldName, mongoOp, compareStr);
  }

  return buildDirectStringComparison(fieldName, mongoOp, compareStr);
}

/**
 * Build gt/gte/lt/lte for calendar-day filter values.
 * @param {string} fieldName - Column name
 * @param {string} operator - V3 filter operator
 * @param {{ day: string, nextDay: string }} dayBounds - Calendar day bounds
 * @param {Object} columnTypeMap - Column type map
 * @returns {Object}
 */
function buildCalendarDayComparison(fieldName, operator, dayBounds, columnTypeMap) {
  switch (operator) {
    case 'greater_than':
      return buildFieldComparison(fieldName, '$gte', dayBounds.nextDay, columnTypeMap, null);
    case 'greater_than_or_equal':
      return buildFieldComparison(fieldName, '$gte', dayBounds.day, columnTypeMap, null);
    case 'less_than':
      return buildFieldComparison(fieldName, '$lt', dayBounds.day, columnTypeMap, null);
    case 'less_than_or_equal':
      return buildFieldComparison(fieldName, '$lt', dayBounds.nextDay, columnTypeMap, null);
    default:
      return null;
  }
}

/**
 * Build comparison / exact / not-equals using string values on both sides.
 * @param {string} fieldName - Column name
 * @param {string} operator - V3 filter operator
 * @param {*} value - Filter value
 * @param {Object} columnTypeMap - Column type map
 * @returns {Object}
 */
function buildTypedComparisonCondition(fieldName, operator, value, columnTypeMap) {
  const compareStr = coerceFilterValueToComparisonString(value);
  const numericValue = coerceNumericFilterValue(value);
  const dayBounds = parseCalendarDay(compareStr);
  const mongoOp = COMPARISON_OPERATOR_MAP[operator];

  if (!mongoOp) {
    return { [fieldName]: convertOperatorToMongoQuery(operator, value) };
  }

  if (dayBounds && DATE_COMPARISON_OPERATORS.has(operator) && isDateField(fieldName, columnTypeMap)) {
    const calendarCondition = buildCalendarDayComparison(
      fieldName,
      operator,
      dayBounds,
      columnTypeMap
    );
    if (calendarCondition) {
      return calendarCondition;
    }
  }

  if (operator === 'exact' && dayBounds && isDateField(fieldName, columnTypeMap)) {
    return {
      $and: [
        buildFieldComparison(fieldName, '$gte', dayBounds.day, columnTypeMap, null),
        buildFieldComparison(fieldName, '$lt', dayBounds.nextDay, columnTypeMap, null),
      ],
    };
  }

  if (operator === 'not_equals' && dayBounds && isDateField(fieldName, columnTypeMap)) {
    return {
      $or: [
        buildFieldComparison(fieldName, '$lt', dayBounds.day, columnTypeMap, null),
        buildFieldComparison(fieldName, '$gte', dayBounds.nextDay, columnTypeMap, null),
      ],
    };
  }

  if (operator === 'exact') {
    if (numericValue !== null && isNumericField(fieldName, columnTypeMap)) {
      return buildNumericExprComparison(fieldName, '$eq', numericValue);
    }
    return buildFieldComparison(fieldName, '$eq', compareStr, columnTypeMap, numericValue);
  }

  if (operator === 'not_equals') {
    if (numericValue !== null && isNumericField(fieldName, columnTypeMap)) {
      return buildNumericExprComparison(fieldName, '$ne', numericValue);
    }
    return buildFieldComparison(fieldName, '$ne', compareStr, columnTypeMap, numericValue);
  }

  if (DATE_COMPARISON_OPERATORS.has(operator)) {
    return buildFieldComparison(fieldName, mongoOp, compareStr, columnTypeMap, numericValue);
  }

  return { [fieldName]: convertOperatorToMongoQuery(operator, value) };
}

/**
 * Build $expr + $regexMatch on $toString for date/amount/number fields.
 * @param {string} fieldName - Column name
 * @param {string} operator - V3 filter operator
 * @param {string} value - Filter value
 * @returns {Object} Match condition with $expr
 */
function buildTypedRegexMatchCondition(fieldName, operator, value) {
  const escaped = escapeRegex(value);
  let regex;

  switch (operator) {
    case 'starts_with':
      regex = `^${escaped}`;
      break;
    case 'ends_with':
      regex = `${escaped}$`;
      break;
    case 'exact':
      regex = `^${escaped}$`;
      break;
    default:
      regex = escaped;
  }

  const regexMatch = {
    $regexMatch: {
      input: getFieldStringExpr(fieldName),
      regex,
      options: 'i',
    },
  };

  if (operator === 'not_contains') {
    return { $expr: { $not: [regexMatch] } };
  }

  return { $expr: regexMatch };
}

/**
 * Regex-style match — $toString for date/number columns, direct $regex for strings.
 * @param {string} fieldName - Column name
 * @param {string} operator - V3 filter operator
 * @param {*} value - Filter value
 * @param {Object} columnTypeMap - Column type map
 * @returns {Object}
 */
function buildFieldRegexCondition(fieldName, operator, value, columnTypeMap) {
  if (shouldUseExprForRegex(fieldName, columnTypeMap)) {
    return buildTypedRegexMatchCondition(fieldName, operator, value);
  }

  const rawValue = coerceFilterValueToComparisonString(value);
  const safeValue = escapeRegex(rawValue);

  return {
    [fieldName]: convertOperatorToMongoQuery(operator, safeValue),
  };
}

/**
 * Null check for typed fields that may be stored as null, missing, or empty string.
 * @param {string} fieldName - Column name
 * @returns {Object} MongoDB match condition
 */
function buildTypedIsNullCondition(fieldName) {
  return {
    $or: [
      { [fieldName]: null },
      { [fieldName]: { $exists: false } },
      { [fieldName]: '' },
    ],
  };
}

/**
 * Not-null check for typed fields that may be stored as null, missing, or empty string.
 * @param {string} fieldName - Column name
 * @returns {Object} MongoDB match condition
 */
function buildTypedIsNotNullCondition(fieldName) {
  return {
    $and: [
      { [fieldName]: { $ne: null } },
      { [fieldName]: { $exists: true } },
      { [fieldName]: { $ne: '' } },
    ],
  };
}

/**
 * Build a single $match condition for one filter column/operator.
 * Date and amount/number fields (from fileConfig columnmap type) get typed handling;
 * all other fields pass through unchanged.
 * @param {string} fieldName - Column name
 * @param {string} operator - V3 filter operator
 * @param {string} value - Filter value
 * @param {Object} columnTypeMap - Map of column names to types
 * @returns {Object} MongoDB match condition
 */
export function buildMatchCondition(fieldName, operator, value, columnTypeMap = {}) {
  const isDate = isDateField(fieldName, columnTypeMap);
  const isNumeric = isNumericField(fieldName, columnTypeMap);
  const isTyped = isDate || isNumeric;

  if (isTyped) {
    if (operator === 'is_null') {
      return buildTypedIsNullCondition(fieldName);
    }

    if (operator === 'is_not_null') {
      return buildTypedIsNotNullCondition(fieldName);
    }

    if (TYPED_REGEX_OPERATORS.has(operator)) {
      return buildFieldRegexCondition(fieldName, operator, value, columnTypeMap);
    }

    if (
      DATE_COMPARISON_OPERATORS.has(operator) ||
      operator === 'exact' ||
      operator === 'not_equals'
    ) {
      return buildTypedComparisonCondition(fieldName, operator, value, columnTypeMap);
    }

    return { [fieldName]: convertOperatorToMongoQuery(operator, value) };
  }

  if (TYPED_REGEX_OPERATORS.has(operator)) {
    return buildFieldRegexCondition(fieldName, operator, value, columnTypeMap);
  }

  if (
    DATE_COMPARISON_OPERATORS.has(operator) ||
    operator === 'exact' ||
    operator === 'not_equals'
  ) {
    return buildTypedComparisonCondition(fieldName, operator, value, columnTypeMap);
  }

  return {
    [fieldName]: convertOperatorToMongoQuery(
      operator,
      coerceFilterValueToComparisonString(value)
    ),
  };
}

/**
 * Build MongoDB query from V3 API filters
 * @param {Object} filters - Filter configuration with groups and connectors
 * @param {string} groupingConnector - Global connector between groups
 * @param {Object} columnTypeMap - Map of column names to types from fileConfig (for date detection)
 * @returns {Object} MongoDB query object
 */
export function buildMongoQueryFromFilters(filters, groupingConnector, columnTypeMap = {}) {
  if (!filters || !filters.groups || filters.groups.length === 0) {
    return {};
  }

  const groupQueries = [];

  // Process each filter group
  for (const group of filters.groups) {
    if (!group.conditions || group.conditions.length === 0) {
      continue;
    }

    const conditionQueries = [];

    // Process each condition in the group
    for (const condition of group.conditions) {
      if (!condition.column) {
        continue;
      }

      conditionQueries.push(
        buildMatchCondition(
          condition.column,
          condition.operator,
          condition.value,
          columnTypeMap
        )
      );
    }

    if (conditionQueries.length === 0) {
      continue;
    }

    // Combine conditions within group using group's connector
    if (conditionQueries.length === 1) {
      groupQueries.push(conditionQueries[0]);
    } else {
      const groupConnectorKey = group.connector === 'AND' ? '$and' : '$or';
      groupQueries.push({
        [groupConnectorKey]: conditionQueries
      });
    }
  }

  if (groupQueries.length === 0) {
    return {};
  }

  // Combine groups using grouping connector
  if (groupQueries.length === 1) {
    return groupQueries[0];
  }

  const groupingConnectorKey = groupingConnector === 'AND' ? '$and' : '$or';
  return {
    [groupingConnectorKey]: groupQueries
  };
}

/**
 * Build MongoDB aggregation pipeline from filters and sort configuration
 * @param {Object} filters - Filter configuration with groups and connector
 * @param {Array} sort - Sort configuration array [{ field: "columnName", direction: "asc"|"desc" }]
 * @param {string} groupingConnector - Global connector between groups
 * @param {Object} columnTypeMap - Map of column names to types from fileConfig (for date detection)
 * @returns {string} MongoDB aggregation pipeline as JSON string
 */
export function buildPipelineFromFiltersAndSort(filters, sort, groupingConnector, columnTypeMap = {}) {
  const pipeline = [];

  // Step 1: Apply filters if provided
  if (filters && filters.groups && filters.groups.length > 0) {
    const matchQuery = buildMongoQueryFromFilters(filters, groupingConnector, columnTypeMap);

    if (Object.keys(matchQuery).length > 0) {
      pipeline.push({ $match: matchQuery });
    }
  }

  // Step 2: Apply sort if provided
  if (sort && Array.isArray(sort) && sort.length > 0) {
    const sortObject = {};

    // Convert sort array to MongoDB sort object
    for (const sortItem of sort) {
      if (sortItem.field && sortItem.direction) {
        // Convert "asc"/"desc" to 1/-1
        const direction = sortItem.direction.toLowerCase() === 'asc' ? 1 : -1;
        sortObject[sortItem.field] = direction;
      }
    }

    // Only add $sort stage if we have valid sort fields
    if (Object.keys(sortObject).length > 0) {
      pipeline.push({ $sort: sortObject });
    }
  }

  return JSON.stringify(pipeline);
}

/**
 * Check if filters have valid content (non-empty conditions with values).
 * @param {Object} filters
 * @returns {boolean}
 */
export function hasValidFilterContent(filters) {
  if (!filters?.groups?.length) return false;

  return filters.groups.some((group) => (
    group.conditions?.some((condition) => {
      if (!condition?.column) return false;
      if (!operatorRequiresValue(condition.operator)) return true;
      if (condition.value === null || condition.value === undefined) return false;
      if (typeof condition.value === 'number') return !Number.isNaN(condition.value);
      return String(condition.value).trim() !== '';
    })
  ));
}

/**
 * Check if sort array has valid content.
 * @param {Array} sort
 * @returns {boolean}
 */
export function hasValidSortContent(sort) {
  return Array.isArray(sort) && sort.length > 0 && sort.some((item) => item?.key || item?.field);
}

/**
 * Serialize API or client-built export filters for the download endpoint.
 * @param {Array|string|null|undefined} filters
 * @returns {string|null}
 */
export function serializeExportDataFilters(filters) {
  if (!filters) return null;
  if (typeof filters === 'string') return filters.length > 0 ? filters : null;
  if (Array.isArray(filters) && filters.length > 0) return JSON.stringify(filters);
  return null;
}

/**
 * Resolve Excel export filters and sort using the same active-tab priority as TMResultsDataTable.
 * Playground tab: playground → applied → saved.
 * Saved tab: applied → saved.
 * Falls back to API export filters when no client filters can be resolved.
 * @param {Object} options
 * @returns {{ filters: Object|null, sort: Array|null }}
 */
export function resolveExportDataFilters({
  lastAppliedFilterTab = null,
  playgroundFilters = null,
  playgroundFilterGroupingConnector = 'OR',
  appliedFilters = null,
  savedFilters = null,
  playgroundSort = null,
  appliedSort = null,
  savedSort = null,
  columnTypeMap = {},
  apiExportFilters = null,
  enableSavedFilterSort = true,
} = {}) {
  const resolvePlaygroundFilters = () => {
    if (!hasValidFilterContent(playgroundFilters)) return null;
    return {
      filters: buildFilterPayload(
        playgroundFilters.groups,
        playgroundFilterGroupingConnector,
        columnTypeMap
      ),
    };
  };

  const resolveAppliedFilters = () => {
    if (!hasValidFilterContent(appliedFilters)) return null;
    return { filters: appliedFilters };
  };

  const resolveSavedFilters = () => {
    if (!hasValidFilterContent(savedFilters)) return null;
    return {
      filters: buildFilterPayload(
        savedFilters.groups,
        savedFilters.group_connectors?.[0] || 'OR',
        columnTypeMap
      ),
    };
  };

  const effectiveLastTab = enableSavedFilterSort ? lastAppliedFilterTab : 'playground';

  let filterResult = null;
  if (effectiveLastTab === 'playground') {
    filterResult = resolvePlaygroundFilters() || resolveAppliedFilters() || resolveSavedFilters();
  } else {
    filterResult = resolveAppliedFilters() || resolveSavedFilters();
  }

  let activeSort = null;
  if (hasValidSortContent(playgroundSort)) {
    activeSort = playgroundSort;
  } else if (hasValidSortContent(appliedSort)) {
    activeSort = appliedSort;
  } else if (hasValidSortContent(savedSort)) {
    activeSort = savedSort;
  }

  let filters = filterResult?.filters || null;
  if (!filters && apiExportFilters?.groups) {
    filters = apiExportFilters;
  }

  const sort = hasValidSortContent(activeSort)
    ? activeSort.map((item) => ({
      field: item.key || item.field,
      direction: item.direction,
    }))
    : null;

  return { filters, sort };
}

// ============================================================================
// UTILITY FUNCTIONS - Validation
// ============================================================================

/**
 * Validate total file size for uploads
 * @param {Array} files - Array of file objects with size property
 * @returns {Object} Validation result with isValid flag and error message
 */
export function validateFileSize(files) {
  const totalSize = files.reduce((size, file) => size + (file.size || 0), 0);
  
  if (totalSize > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: ERROR_MESSAGES.FILE_SIZE_EXCEEDED
    };
  }
  
  return { isValid: true };
}

/**
 * Validate file extensions
 * @param {Array} files - Array of file objects with name property
 * @returns {Object} Validation result with isValid flag and error message
 */
export function validateFileExtensions(files) {
  for (const file of files) {
    const fileExtension = (file?.name || '').split('.').pop().toLowerCase();
    if (!ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
      return {
        isValid: false,
        error: `File '${file.name}' ${ERROR_MESSAGES.UNSUPPORTED_FILE_TYPE}`
      };
    }
  }
  
  return { isValid: true };
}

/**
 * Validate files for bulk manual matching
 * Checks both size and extension requirements
 * 
 * @param {Array} files - Array of file objects
 * @returns {Object} Validation result with isValid flag and error message
 */
export function validateBulkMatchFiles(files) {
  // Validate size
  const sizeValidation = validateFileSize(files);
  if (!sizeValidation.isValid) {
    return sizeValidation;
  }
  
  // Validate extensions
  const extensionValidation = validateFileExtensions(files);
  if (!extensionValidation.isValid) {
    return extensionValidation;
  }
  
  return { isValid: true };
}

// ============================================================================
// UTILITY FUNCTIONS - Data Extraction
// ============================================================================

/**
 * Get consolidated collection name from annexure list
 * @param {Array} annexureList - Array of annexure items
 * @returns {string} Collection name or empty string if not found
 */
export function getConsolidatedCollection(annexureList) {
  if (!annexureList || !Array.isArray(annexureList)) {
    return '';
  }
  const consolidatedItem = annexureList.find(
    item => item && item.name === 'Consolidated Annexure'
  );
  return consolidatedItem?.collection || '';
}

/**
 * Transform bulk match error response to display format
 * @param {Object} responseData - API error response data
 * @returns {Object} Object with errorMessage and errors array
 */
export function transformBulkMatchErrors(responseData) {
  const errorMessage = responseData.message || ERROR_MESSAGES.VALIDATION_FAILED;
  let errors = [];
  
  // Extract row-level errors if available
  if (Array.isArray(responseData.data) && responseData.data.length > 0) {
    errors = responseData.data.map(item => ({
      row: item.row,
      sheet: item.sheet || '',
      source: item.source || '',
      table1Sno: item.table1Sno || '',
      table2Sno: item.table2Sno || '',
      column: item.column || '',
      error: item.error || ''
    }));
  }
  
  return { errorMessage, errors };
}
